import { queryKeys } from '@qse/api'
import { WebSocketMessage } from '@qse/constants'
import { useQueryClient } from '@tanstack/react-query'
import { useCallback, useEffect, useRef } from 'react'

import { createEventHandlers, processWebSocketMessage } from './event-handlers'
import type { RealtimeConfig, RealtimeHookReturn } from './types'
import { getGlobalWebSocketManager } from './websocket-manager'

/**
 * React hook for managing real-time WebSocket connections
 */
export function useRealtime(config: RealtimeConfig): RealtimeHookReturn {
  const queryClient = useQueryClient()
  const managerRef = useRef<ReturnType<typeof getGlobalWebSocketManager> | null>(null)

  // Create event handlers
  const eventHandlers = useCallback(() => {
    const routeContext = config.getRouteContext()

    return createEventHandlers({
      queryClient,
      queryKeys,
      routeContext,
      eventHandlers: config.eventHandlers,
    })
  }, [config, queryClient])

  // Create message handler
  const messageHandler = useCallback(
    (message: WebSocketMessage) => {
      const handlers = eventHandlers()

      processWebSocketMessage(message, handlers)
    },
    [eventHandlers],
  )

  // Initialize or update global WebSocket manager
  useEffect(() => {
    managerRef.current = getGlobalWebSocketManager(config, messageHandler)
  }, [config, messageHandler])

  // Connection management
  const connect = useCallback(async () => {
    if (managerRef.current) {
      await managerRef.current.connect()
    }
  }, [])

  const disconnect = useCallback(async () => {
    if (managerRef.current) {
      await managerRef.current.disconnect()
    }
  }, [])

  // Get connection state
  const getConnectionState = useCallback(() => {
    if (managerRef.current) {
      return managerRef.current.getConnectionState()
    }

    return {
      isConnected: false,
      isConnecting: false,
      error: null,
      connectionId: null,
    }
  }, [])

  // Check authentication status and connect/disconnect as needed
  const checkAuthAndConnect = useCallback(async () => {
    if (!config.shouldConnect()) {
      // If we shouldn't connect but are connected, disconnect
      const connectionState = getConnectionState()

      if (connectionState.isConnected || connectionState.isConnecting) {
        console.log('🔌 App should not connect, disconnecting WebSocket...')
        await disconnect()
      }

      return
    }

    try {
      const token = await config.getToken()
      const connectionState = getConnectionState()

      if (token) {
        // If we have a token but no connection, try to connect
        if (!connectionState.isConnected && !connectionState.isConnecting) {
          console.log('🔄 Authentication detected, attempting WebSocket connection...')
          await connect()
        } else if (connectionState.isConnected) {
          console.log('✅ WebSocket already connected with valid token')
        } else if (connectionState.isConnecting) {
          console.log('🔄 WebSocket connection already in progress')
        }
      } else {
        // If we don't have a token but are connected, disconnect
        if (connectionState.isConnected || connectionState.isConnecting) {
          console.log('🔌 No authentication token, disconnecting WebSocket...')
          await disconnect()
        }
      }
    } catch (error) {
      console.warn('Error checking authentication status:', error)
      // If there's an error getting the token, disconnect to be safe
      const connectionState = getConnectionState()

      if (connectionState.isConnected || connectionState.isConnecting) {
        console.log('🔌 Auth error detected, disconnecting WebSocket...')
        await disconnect()
      }
    }
  }, [config, getConnectionState, connect, disconnect])

  // Auto-connect logic with event-driven authentication monitoring
  useEffect(() => {
    // Initial connection attempt only if we should connect
    if (config.shouldConnect()) {
      checkAuthAndConnect()
    }

    // Listen for storage changes (login/logout events)
    const handleStorageChange = (event: StorageEvent) => {
      if (
        event.key === 'auth_token' ||
        event.key === 'user_session' ||
        event.key?.includes('auth') ||
        event.key === 'token'
      ) {
        console.log('🔄 Storage change detected, checking authentication status...')
        // Add a small delay to ensure the storage is fully updated
        setTimeout(() => {
          checkAuthAndConnect()
        }, 100)
      }
    }

    // Listen for custom auth events (triggered after login/logout)
    const handleAuthEvent = () => {
      console.log('🔄 Auth event detected, checking authentication status...')
      // Add a small delay to ensure all auth state is updated
      setTimeout(async () => {
        try {
          const token = await config.getToken()

          if (!token) {
            console.log('🔌 No token available, disconnecting...')
            await disconnect()

            return
          }
          checkAuthAndConnect()
        } catch (error) {
          console.log('🔌 Auth check failed, disconnecting...')
          await disconnect()
        }
      }, 100)
    }

    // Listen for page focus to check auth status when user returns to tab
    const handleFocus = () => {
      console.log('🔄 Page focus detected, checking authentication status...')
      checkAuthAndConnect()
    }

    window.addEventListener('storage', handleStorageChange)
    window.addEventListener('auth-changed', handleAuthEvent)
    window.addEventListener('focus', handleFocus)

    return () => {
      // Clean up event listeners
      window.removeEventListener('storage', handleStorageChange)
      window.removeEventListener('auth-changed', handleAuthEvent)
      window.removeEventListener('focus', handleFocus)
    }
  }, [checkAuthAndConnect, config])

  // Cleanup on unmount only (separate effect to avoid re-running)
  useEffect(() => {
    return () => {
      // Only destroy global manager when the component completely unmounts
      // This happens when navigating away from the app entirely
      console.log('🔌 useRealtime unmounting, cleaning up...')
      if (managerRef.current) {
        managerRef.current.disconnect()
      }
    }
  }, [])

  return {
    connectionState: getConnectionState(),
    connect,
    disconnect,
  }
}
